import { useTranslation } from 'react-i18next'
import { Bot, MessageSquare } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { MessageList } from './message-list'
import { ChatInput } from './chat-input'
import type { Conversation, Message } from '@/schemas/chat'

interface ChatAreaProps {
  conversation: Conversation | null
  messages: Message[]
  isSending: boolean
  isLoadingMessages: boolean
  error: string | null
  onSendMessage: (content: string) => void
}

export function ChatArea({
  conversation,
  messages,
  isSending,
  isLoadingMessages,
  error,
  onSendMessage,
}: ChatAreaProps) {
  const { t } = useTranslation('chat')

  if (!conversation) {
    return (
      <Card variant="glass" className="flex-1 flex flex-col items-center justify-center rounded-2xl">
        <MessageSquare className="h-12 w-12 text-gray-500 mb-4" />
        <h3 className="text-lg font-medium text-gray-300">
          {t('messages.emptyState')}
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-sm text-center">
          {t('messages.emptyStateDesc')}
        </p>
      </Card>
    )
  }

  return (
    <Card variant="glass" className="flex-1 flex flex-col rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-700/50 px-6 py-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-500/20">
          <Bot className="h-5 w-5 text-indigo-400" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-medium text-gray-200 truncate">
            {conversation.agent_name || 'Agent'}
          </h3>
          <div className="flex items-center gap-1.5 mt-0.5">
            <div className="h-2 w-2 rounded-full bg-emerald-400" />
            <span className="text-xs text-gray-400">Online</span>
          </div>
        </div>
      </div>

      {/* Messages */}
      {isLoadingMessages ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-sm text-gray-400 animate-pulse">
            {t('messages.typing')}
          </div>
        </div>
      ) : (
        <MessageList messages={messages} isSending={isSending} />
      )}

      {/* Error */}
      {error && (
        <div className="px-4 py-2 bg-red-500/10 border-t border-red-500/20">
          <p className="text-xs text-red-400">{error}</p>
        </div>
      )}

      {/* Input */}
      <ChatInput onSend={onSendMessage} disabled={isSending} />
    </Card>
  )
}
